<template>
  <div>
    <app-button
      variant="danger"
      @click="isOpen = true"
      data-locator="close-room-button"
    >
      <app-material-icon name="block" color="inherit" />
      {{ closeRoomButtonText }}
    </app-button>
    <app-modal v-model="isOpen"
               size="md"
               :ok-text="$t('common.confirm')"
               :ok-loading="closeRoomPending"
               :ok-disabled="closeRoomPending"
               :cancel-disabled="closeRoomPending"
               data-locator="settings-close-room-dialog"
               @ok="prepareCloseRoom"
               @cancel="resetAndClose"
    >
      <template #title>
        {{ $t('project.project-settings.ProjectSettingsCloseRoomDialog.closeProject') }}
      </template>
      <template #body>
        <app-alert-in-page
          v-if="closingInProgress"
          class="mb-6"
          variant="warning"
          :dismissible="false"
        >
          {{ $t('project.project-settings.ProjectSettingsCloseRoomDialog.closingInProgressWarning') }}
        </app-alert-in-page>
        <v-row>
          <v-col>
            <i18n-t keypath="project.project-settings.ProjectSettingsCloseRoomDialog.text1"
                    tag="p"
                    class="mb-3"
                    scope="global"
            >
              <template #warning>
                <span class="font-weight-bold">{{ $t("common.warning") }}</span>
              </template>
            </i18n-t>
            <p class="mb-3">{{ $t("project.project-settings.ProjectSettingsCloseRoomDialog.text2") }}</p>
            <p class="font-weight-bold">
              {{ $t('project.project-settings.ProjectSettingsCloseRoomDialog.text3', {date: deletionDate}) }}
            </p>

            <p class="mt-6">{{ $t('project.project-settings.ProjectSettingsCloseRoomDialog.notify') }}</p>
            <v-checkbox v-model="notifyPms"
                        :label="$t('project.project-settings.ProjectSettingsCloseRoomDialog.notifyPms')"
                        :disabled="forcedNotifications"
                        hide-details
                        data-locator="notify-project-managers-checkbox"
            />
            <v-checkbox v-model="notifyGuests"
                        :label="$t('project.project-settings.ProjectSettingsCloseRoomDialog.notifyGuests')"
                        :disabled="forcedNotifications"
                        hide-details
                        data-locator="notify-guests-checkbox"
            />
          </v-col>
        </v-row>
      </template>
    </app-modal>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { mapActions, mapGetters, mapState } from 'vuex'

import { track } from '@/common/pendo/agent'
import { ISOToDDMMYYYY } from '@/common/utils/dates'
import { PRODUCT_ROOM_TYPES } from '@/common/utils/roomTypes'
import { toast } from '@/common/utils/toast'
import { ROOMS_ROUTE_NAME } from '@/router/routerData'
import { CLOSE_ROOM } from '@/store/modules/room/action_types'

export default {
  name: 'ProjectSettingsCloseRoomDialog',
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      isOpen: false,
      notifyPms: true,
      notifyGuests: true,
    }
  },
  computed: {
    ...mapState('room', ['currentRoom', 'closeRoomPending']),
    ...mapGetters('room', ['isDataRoom']),
    ...mapGetters('user', ['areNotificationsForcedOnRoomClose']),
    deletionDate () {
      return ISOToDDMMYYYY(dayjs().add(1, 'month'))
    },
    closeRoomButtonText () {
      return this.isDataRoom
        ? this.$t('project.project-settings.ProjectSettingsCloseRoomDialog.closeDataRoom')
        : this.$t('project.project-settings.ProjectSettingsCloseRoomDialog.closeProject')
    },
    forcedNotifications () {
      return this.areNotificationsForcedOnRoomClose
    },
    closingInProgress () {
      return this.currentRoom.inClosing
    },
  },
  methods: {
    ...mapActions('room', [CLOSE_ROOM]),
    resetAndClose () {
      this.isOpen = false
      this.notifyPms = true
      this.notifyGuests = true
    },
    async prepareCloseRoom () {
      try {
        await this.CLOSE_ROOM({
          mnemo: this.mnemo,
          data: {
            notifyPms: this.notifyPms,
            notifyGuests: this.notifyGuests,
          },
        })
        toast.success(this.$t('project.project-settings.ProjectSettingsCloseRoomDialog.closeRoomSuccess'))
        track('project_closed', {
          project_mnemo: this.mnemo,
          project_type: this.isDataRoom ? PRODUCT_ROOM_TYPES.DATAROOM : PRODUCT_ROOM_TYPES.LEGAL_PROJECT,
          project_duration: dayjs().diff(dayjs(this.currentRoom.createdAt), 'day'),
        })
        this.$router.push({ name: ROOMS_ROUTE_NAME })
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.project-settings.ProjectSettingsCloseRoomDialog.closeRoomError'))
      }
    },
  },
}
</script>
